import { useEffect } from 'react';

import { ScreenName } from '@heidi/runtime/analytics-vocabulary';
import { useOnboardingContext } from '../../../context/OnboardingContext';

import { LearnBasicsDocumentPicker } from '../LearnBasicsDocumentPicker';
import { useLearnBasicsTaskActions } from '../useLearnBasicsTaskActions';
import { tourStepsMessages } from './tour-steps.messages';
import { TourStepShell } from './TourStepShell';

interface TourArtefactsStepProps {
  onContinue: () => void;
  onSkip: () => void;
}

/**
 * Guided-tour screen 4: the create-document task, promoted to its own screen.
 * Each checked document opens its own tab in the preview.
 */
export function TourArtefactsStep({ onContinue, onSkip }: TourArtefactsStepProps) {
  const { micTest } = useOnboardingContext();
  const { documentOptions, toggleDocument, isNoteGenerating } =
    useLearnBasicsTaskActions();

  // Focus whichever surface this screen is about: an already-generated document
  // when the user comes back to it, otherwise the note they're generating from.
  const { setNoteTab } = micTest;
  const firstSelectedDocument = micTest.selectedDocuments[0];
  useEffect(() => {
    setNoteTab(
      firstSelectedDocument === undefined ? 'note' : `doc-${firstSelectedDocument}`
    );
    // Only on entry — `toggleDocument` owns the tab while the user is here.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setNoteTab]);

  return (
    <TourStepShell
      screenName={ScreenName.OnboardingTourArtefacts}
      title={tourStepsMessages.artefactsTitle}
      subtitle={tourStepsMessages.artefactsSubtitle}
      onContinue={onContinue}
      onSkip={onSkip}
      // A document is generated *from* the note, so picking one before the note
      // lands has nothing to work from — the same guard the detail screen uses.
      // Once the note exists, Continue only waits for a pick, not for the
      // document to finish writing: Skip is always live beside it, so blocking
      // longer just pushes impatient finishers into the skipped bucket.
      continueDisabled={isNoteGenerating || !micTest.editActivity.createdDocument}
      continueHint={
        isNoteGenerating
          ? tourStepsMessages.noteGeneratingHint
          : tourStepsMessages.artefactsContinueHint
      }
    >
      <LearnBasicsDocumentPicker
        options={documentOptions}
        selected={micTest.selectedDocuments}
        onToggle={toggleDocument}
        disabled={isNoteGenerating}
      />
    </TourStepShell>
  );
}
